import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { EndReason, EndReasonEditResource, EndReasonViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { EndReasonPageBody } from "./EndReasonPageBody";
import { EndReasonPageHeader } from "./EndReasonPageHeader";

const routeName = "endReason";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const endReasonPageRoute: PageRoute<Params> = {
  handle: "End Reason",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const EndReasonPage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [endReason, setEndReason] = useState<EndReason>({} as EndReason);
  const [initEndReason, setInitEndReason] = useState<EndReason>(endReason);
  const [viewResource, setViewResource] = useState<EndReasonViewResource>({});
  const [editResource, setEditResource] = useState<EndReasonEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [endReason] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.endReasons.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.endReasons.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${endReasonPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.endReasons.get(id).then((response) => {
      setEndReason(response);
      setInitEndReason(response);
      resetUnsavedChanges([response]);
    });
  };

  return (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="sm"
      header={
        <EndReasonPageHeader
          rootPermission={ROOT_PERMISSION}
          handle={endReasonPageRoute.handle}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          endReason={endReason}
          setEndReason={setEndReason}
          initEndReason={initEndReason}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
          resetUnsavedChanges={resetUnsavedChanges}
        />
      }
      content={
        <EndReasonPageBody
          endReason={endReason}
          setEndReason={setEndReason}
          viewResource={viewResource}
          editResource={editResource}
          crudMode={params.crudMode}
          validationErrors={validationErrors}
        />
      }
    />
  );
};
